import React, { useState } from "react";
import { Undo2, Eraser, ChevronLeft, ChevronRight } from "lucide-react";
import { LETTERS } from "@/lib/music";
import { beamGroups, fitBeam, groupUp } from "@/lib/beams";

const W = 520, TOP = 40, GAP = 7, X0 = 70;
const REF = 38; // F5 on the top line
const LENGTHS = [[0.5, "Eighth"], [1, "Quarter"], [2, "Half"], [3, "Dotted half"], [4, "Whole"]];
const idxToNote = (i, acc) => `${LETTERS[((i % 7) + 7) % 7]}${acc}${Math.floor(i / 7)}`;
const noteToIdx = (n) => { const m = n.match(/^([A-G])([b#]?)(\d)$/); return Number(m[3]) * 7 + LETTERS.indexOf(m[1]); };

export const MelodyTapIn = ({ bars, setBars, beatsPerBar, barCount, testId }) => {
  const [cur, setCur] = useState(0);
  const [len, setLen] = useState(1);
  const [acc, setAcc] = useState("");
  const [msg, setMsg] = useState("");
  const bar = bars[cur] || [];
  const used = bar.reduce((a, [, d]) => a + d, 0);

  const add = (note) => {
    if (used + len > beatsPerBar + 1e-6) { setMsg(`Bar ${cur + 1} only has ${beatsPerBar - used} beat(s) left`); return; }
    setMsg("");
    const next = [...bars];
    next[cur] = [...bar, [note, len]];
    setBars(next);
    if (Math.abs(used + len - beatsPerBar) < 1e-6 && cur < barCount - 1) setCur(cur + 1);
  };
  const edit = (fn) => { const next = [...bars]; next[cur] = fn(bar); setBars(next); setMsg(""); };
  const clickStaff = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const y = ((e.clientY - r.top) / r.height) * 150;
    const i = Math.round(REF - (y - TOP) / GAP);
    if (i < 24 || i > 43) return;
    add(idxToNote(i, acc));
  };

  const xs = [];
  bar.reduce((x, [, d]) => { xs.push(x); return x + 18 + d * 40; }, X0);
  const tips = {}, tapBeams = [];
  let at = 0;
  const items = bar.map(([n, d]) => { const it = { pos: at, len: d, beam: n !== "r" && d === 0.5 }; at += d; return it; });
  beamGroups(items, beatsPerBar === 3 ? 1 : 2).forEach((g) => {
    const ys = g.map((k) => TOP + (REF - noteToIdx(bar[k][0])) * GAP);
    const up = groupUp(ys.map((y) => ({ top: y, bottom: y })), TOP + 28);
    const pts = g.map((k, j) => ({ x: xs[k] + (up ? 6 : -6), top: ys[j], bottom: ys[j] }));
    const line = fitBeam(pts, up, 30);
    g.forEach((k, j) => { tips[k] = { y: line.at(pts[j].x), up }; });
    tapBeams.push({ x0: pts[0].x - 0.7, y0: line.at(pts[0].x), x1: pts[pts.length - 1].x + 0.7, y1: line.at(pts[pts.length - 1].x), t: up ? 4.5 : -4.5 });
  });
  return (
    <div className="rounded-xl border border-border bg-background p-3" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        {LENGTHS.map(([d, l]) => (
          <button key={d} type="button" onClick={() => setLen(d)} data-testid={`tap-len-${d}`}
            className={`rounded-full px-3 py-1 ${len === d ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>{l}</button>
        ))}
        {[["", "♮"], ["#", "♯"], ["b", "♭"]].map(([a, l]) => (
          <button key={l} type="button" onClick={() => setAcc(a)} data-testid={`tap-acc-${l}`}
            className={`rounded-full px-2.5 py-1 ${acc === a ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>{l}</button>
        ))}
        <button type="button" onClick={() => add("r")} data-testid="tap-rest" className="rounded-full border border-border px-3 py-1 text-muted-foreground hover:border-primary">Add rest</button>
      </div>
      <svg viewBox={`0 0 ${W} 150`} className="mt-2 w-full cursor-crosshair" onClick={clickStaff} data-testid="tap-staff">
        {[0, 1, 2, 3, 4].map((k) => <line key={k} x1={10} x2={W - 10} y1={TOP + k * 14} y2={TOP + k * 14} stroke="var(--staff-line)" strokeWidth="1.1" />)}
        <text x={14} y={TOP + 56} fontSize="56" fill="var(--staff-note)" fontFamily="'Noto Music', serif">𝄞</text>
        {bar.map(([n, d], k) => {
          const nx = xs[k];
          if (n === "r") return <rect key={k} x={nx - 6} y={TOP + 18} width={12} height={8} fill="hsl(var(--primary))" />;
          const y = TOP + (REF - noteToIdx(n)) * GAP;
          return (
            <g key={k}>
              {[70, 84, 98].filter((o) => y >= TOP + o - 1).concat([-14, -28].filter((o) => y <= TOP + o + 1)).map((o) => <line key={o} x1={nx - 11} x2={nx + 11} y1={TOP + o} y2={TOP + o} stroke="var(--staff-line)" />)}
              {n.length === 3 && <text x={nx - 20} y={y + 5} fontSize="16" fill="hsl(var(--primary))">{n[1] === "#" ? "♯" : "♭"}</text>}
              <ellipse cx={nx} cy={y} rx="6.5" ry="4.8" transform={`rotate(-20 ${nx} ${y})`} fill={d >= 2 ? "var(--staff-hollow)" : "hsl(var(--primary))"} stroke="hsl(var(--primary))" strokeWidth="1.6" />
              {d < 4 && (tips[k] && !tips[k].up
                ? <line x1={nx - 6} x2={nx - 6} y1={y} y2={tips[k].y} stroke="hsl(var(--primary))" strokeWidth="1.5" />
                : <line x1={nx + 6} x2={nx + 6} y1={y} y2={tips[k] ? tips[k].y : y - 30} stroke="hsl(var(--primary))" strokeWidth="1.5" />)}
              {d === 3 && <circle cx={nx + 12} cy={y} r="1.8" fill="hsl(var(--primary))" />}
              {d === 0.5 && tips[k] == null && <path d={`M${nx + 6} ${y - 30} q10 10 4 22`} stroke="hsl(var(--primary))" strokeWidth="1.5" fill="none" />}
            </g>
          );
        })}
        {tapBeams.map((b, k) => <polygon key={`b${k}`} points={`${b.x0},${b.y0} ${b.x1},${b.y1} ${b.x1},${b.y1 + b.t} ${b.x0},${b.y0 + b.t}`} fill="hsl(var(--primary))" data-testid={`tap-beam-${k}`} />)}
      </svg>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <button type="button" onClick={() => setCur(Math.max(0, cur - 1))} data-testid="tap-prev" className="rounded-full border border-border p-1.5"><ChevronLeft size={14} /></button>
        <span data-testid="tap-bar-label">Bar {cur + 1} of {barCount} · {used}/{beatsPerBar} beats</span>
        <button type="button" onClick={() => setCur(Math.min(barCount - 1, cur + 1))} data-testid="tap-next" className="rounded-full border border-border p-1.5"><ChevronRight size={14} /></button>
        <button type="button" onClick={() => edit((b) => b.slice(0, -1))} data-testid="tap-undo" className="flex items-center gap-1 rounded-full border border-border px-3 py-1"><Undo2 size={12} /> Undo</button>
        <button type="button" onClick={() => edit(() => [])} data-testid="tap-clear" className="flex items-center gap-1 rounded-full border border-border px-3 py-1"><Eraser size={12} /> Clear bar</button>
        {msg && <span className="text-red-500" data-testid="tap-msg">{msg}</span>}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">Pick a length (and ♯/♭), then click a line or space. Full bars move on automatically.</p>
    </div>
  );
};
